import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import config from "../config";
import { getAuthToken, getAuthUser } from "../hooks/useAuthToken";

const { api } = config;

axios.defaults.baseURL = api.API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json";

const handleUnauthorized = () => {
  localStorage.removeItem("authUser");
  window.location.href = "/login";
};

const initialToken = getAuthToken();
if (initialToken) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + initialToken;
}

axios.interceptors.request.use(
  function (config) {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;

    const status = error.response?.status;

    if (status === 401) {
      message = "Session expirée. Veuillez vous reconnecter.";
      handleUnauthorized();
      return Promise.reject({ message, redirect: true });
    }

    const apiMessage = error.response?.data?.message;

    switch (status) {
      case 500:
        message = apiMessage || "Erreur interne du serveur";
        break;
      case 404:
        message = apiMessage || "Données introuvables";
        break;
      case 400:
        message = apiMessage || "Requête invalide";
        break;
      case 503:
        message = apiMessage || "Service temporairement indisponible";
        break;
      default:
        message = apiMessage || error.message || error;
    }

    return Promise.reject(message);
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

/**
 * Builds the standard authenticated headers used across apiV2 calls.
 * The Authorization header will also be injected by the axios interceptor,
 * but returning it here keeps raw fetch() callers on the same contract.
 */
const buildAuthHeaders = (): Record<string, string> => {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Standard axios config for apiV2 POST calls: JSON body + credentials.
 */
const apiV2PostConfig = (): AxiosRequestConfig => ({
  headers: buildAuthHeaders(),
  withCredentials: true,
});

class APIClient {
  /**
   * Fetches data from given url
   */
  get = (url: string, params?: any): Promise<AxiosResponse> => {
    let response: Promise<AxiosResponse>;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };

  /**
   * post given data to url
   */
  create = (url: string, data: any) => {
    return axios.post(url, data);
  };

  /**
   * Updates data
   */
  update = (url: string, data: any) => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any) => {
    return axios.put(url, data);
  };

  /**
   * Delete
   */
  delete = (url: string, config?: AxiosRequestConfig): Promise<AxiosResponse> => {
    return axios.delete(url, { ...config });
  };
}

/**
 * Get logged in user from localStorage
 */
const getLoggedinUser = () => getAuthUser();

/**
 * Check if user is authenticated
 */
const isUserAuthenticated = () => {
  const user = getLoggedinUser();
  return user && user.token ? true : false;
};

export {
  APIClient,
  setAuthorization,
  getLoggedinUser,
  isUserAuthenticated,
  buildAuthHeaders,
  apiV2PostConfig,
};
